import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import api from '../../../api.js';

const TYPE_TO_CATEGORY = {
  broker: 'broker',
  agent: 'agent',
  insurer_direct: 'insurer',
  other: 'broker',
};

function generateCode(category) {
  const prefix = category === 'insurer' ? 'INS' : category === 'agent' ? 'AGT' : 'BRO';
  return `${prefix}-${Date.now().toString(36).slice(-4).toUpperCase()}`;
}

function IntermediaryForm({ intermediary, onClose, onSuccess, onMessage }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(intermediary || {
    name: '',
    type: 'broker',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const category = TYPE_TO_CATEGORY[formData.type] || 'broker';
      const supplierData = {
        name: formData.name,
        category,
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        postal_code: formData.postal_code,
        city: formData.city,
        notes: formData.notes,
        status: 'active',
      };

      if (intermediary?.id) {
        await api.supplier.update(intermediary.id, supplierData);
        onMessage?.({ title: t('insuranceConfig.toast.successTitle'), message: t('insuranceConfig.intermediaryForm.updatedSuccess'), type: 'success' });
      } else {
        supplierData.code = generateCode(category);
        await api.supplier.create(supplierData);
        onMessage?.({ title: t('insuranceConfig.toast.successTitle'), message: t('insuranceConfig.intermediaryForm.createdSuccess'), type: 'success' });
      }
      onSuccess();
    } catch (error) {
      onMessage?.({ title: t('insuranceConfig.toast.errorTitle'), message: t('insuranceConfig.toast.saveFailed'), type: 'error' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex justify-between items-center">
          <h3 className="text-2xl font-bold text-white">
            {intermediary ? t('insuranceConfig.intermediaryForm.titleEdit') : t('insuranceConfig.intermediaryForm.titleCreate')}
          </h3>
          <button onClick={onClose} className="text-white hover:bg-blue-700 p-2 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.name')}</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.type')}</label>
              <select
                required
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="broker">{t('insuranceConfig.intermediaryTypes.broker')}</option>
                <option value="agent">{t('insuranceConfig.intermediaryTypes.agent')}</option>
                <option value="insurer_direct">{t('insuranceConfig.intermediaryTypes.insurer_direct')}</option>
                <option value="other">{t('insuranceConfig.intermediaryTypes.other')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.companyName')}</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({...formData, company_name: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.contactName')}</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.phone')}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.email')}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.address')}</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.postalCode')}</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.city')}</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('insuranceConfig.intermediaryForm.notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              {intermediary ? t('common.actions.edit') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IntermediaryForm;
